import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Zap, 
  Power, 
  PowerOff, 
  Settings, 
  RotateCcw,
  Monitor,
  Lightbulb,
  Fan,
  Speaker
} from 'lucide-react'

const LoadControlPanel = ({ 
  isConnected, 
  sendCommand, 
  deviceStatus 
}) => {
  const [gpioStates, setGpioStates] = useState({
    D0: false,
    D1: false,
    D2: false,
    D3: false,
    D4: false,
    D5: false,
    D6: false,
    D7: false,
    D8: false,
    A0: false
  })

  const gpioButtons = [
    { id: 'D0', name: 'GPIO D0', icon: Zap, color: 'from-yellow-400 to-orange-400' },
    { id: 'D1', name: 'GPIO D1', icon: Monitor, color: 'from-blue-500 to-purple-500' },
    { id: 'D2', name: 'GPIO D2', icon: Power, color: 'from-green-500 to-emerald-500' },
    { id: 'D3', name: 'GPIO D3', icon: Lightbulb, color: 'from-red-500 to-pink-500' },
    { id: 'D4', name: 'GPIO D4', icon: Fan, color: 'from-indigo-500 to-blue-500' },
    { id: 'D5', name: 'GPIO D5', icon: Speaker, color: 'from-purple-500 to-pink-500' },
    { id: 'D6', name: 'GPIO D6', icon: Zap, color: 'from-red-500 to-pink-500' },
    { id: 'D7', name: 'GPIO D7', icon: Power, color: 'from-indigo-500 to-blue-500' },
    { id: 'D8', name: 'GPIO D8', icon: Lightbulb, color: 'from-yellow-400 to-orange-400' },
    { id: 'A0', name: 'GPIO A0', icon: Fan, color: 'from-blue-400 to-cyan-400' }
  ]

  const handleGpioToggle = async (gpioId) => {
    if (!isConnected) return

    const newState = !gpioStates[gpioId]
    setGpioStates(prev => ({ ...prev, [gpioId]: newState }))

    try {
      // Use the correct GPIO command like Flutter app
      await sendCommand('setPinState', { pin: gpioId, state: newState })
    } catch (error) {
      console.error('Failed to toggle GPIO:', error)
      // Revert state on error
      setGpioStates(prev => ({ ...prev, [gpioId]: !newState }))
    }
  }

  const handleResetAll = async () => {
    if (!isConnected) return

    try {
      await sendCommand('resetAllPins')
      setGpioStates({
        D0: false,
        D1: false,
        D2: false,
        D3: false,
        D4: false,
        D5: false,
        D6: false,
        D7: false,
        D8: false,
        A0: false
      })
    } catch (error) {
      console.error('Failed to reset GPIOs:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Load Control</h2>
        <p className="text-gray-400">
          {isConnected ? 'Control your GPIO devices' : 'Connect to a device first'}
        </p>
      </div>

      {/* GPIO Control Grid */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">GPIO Control</h3>
          <button
            onClick={handleResetAll}
            disabled={!isConnected}
            className="btn-secondary flex items-center space-x-2"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset All</span>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {gpioButtons.map((pin) => {
            const Icon = pin.icon
            const isActive = gpioStates[pin.id]
            
            return (
              <motion.button
                key={pin.id}
                onClick={() => handleGpioToggle(pin.id)}
                disabled={!isConnected}
                className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                  isActive 
                    ? `bg-gradient-to-br ${pin.color} border-white/50 shadow-glow` 
                    : 'bg-white/10 border-white/20 hover:border-white/40'
                } ${!isConnected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                whileHover={isConnected ? { scale: 1.05 } : {}}
                whileTap={isConnected ? { scale: 0.95 } : {}}
              >
                <div className="text-center space-y-2">
                  <Icon className={`w-8 h-8 mx-auto ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  <div className="text-sm font-medium">{pin.name}</div>
                  <div className={`text-xs ${isActive ? 'text-white' : 'text-gray-500'}`}>
                    {isActive ? 'ON' : 'OFF'}
                  </div>
                </div>
                
                {/* Status indicator */}
                <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
                  isActive ? 'bg-green-400 shadow-glow-green' : 'bg-gray-500'
                }`} />
              </motion.button>
            )
          })}
        </div>
      </div>

      {/* Connection Status */}
      <div className="card p-4">
        <h3 className="text-lg font-semibold mb-4">Connection Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Mode:</span>
            <span className="font-medium capitalize">Connected</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Status:</span>
            <div className="flex items-center space-x-2">
              <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`} />
              <span className={isConnected ? 'text-success-400' : 'text-danger-400'}>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
          {deviceStatus && (
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Signal:</span>
              <span className="font-medium">{deviceStatus.signal || 'N/A'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoadControlPanel